import { COLOR_PRIMARY } from 'src/utils/const';
import { LEARN_MORE_LINK, KPI_DEFINITIONS } from 'src/utils/kpiDefinitions';
import { FinancialKPIConfig } from '../propTypes';
import {
  BAR_LESS_THAN_20_PERCENT,
  BAR_LESS_THAN_70_PERCENT,
  BAR_MORE_THAN_70_PERCENT,
} from 'src/utils/colors';

export interface MarketMetrics {
  marketData: Record<string, FinancialKPIConfig>;
  income: Record<string, FinancialKPIConfig>;
}

export interface FinancialInputs {
  expenses: Record<string, FinancialKPIConfig>;
  initialInvestment: Record<string, FinancialKPIConfig>;
}

export interface PerformanceIndicators {
  cashflow: Record<string, FinancialKPIConfig>;
  investmentMetricts: Record<string, FinancialKPIConfig>;
}

const LEARN_MORE_TEXT = "Learn more about our data methodology and limitations here.";

export const marketMetrics: MarketMetrics = {
  marketData: {
    occupancyRate: {
      displayName: 'Occupancy Rate',
      key: 'ltm_occupancy_rate',
      unit: '%',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Occupancy Rate',
        timePeriod: 'ltm',
        source: '',
        definition: [...KPI_DEFINITIONS.Occupancy_market_aggregated.definitionLinesWithoutRange] as string[],
        ctaText: KPI_DEFINITIONS.Occupancy_market_aggregated.learnMoreLinkText,
        ctaURL: KPI_DEFINITIONS.Occupancy_market_aggregated.ctaUrl,
      },
    },
    nightlyRate: {
      displayName: 'Nightly Rate',
      key: 'ltm_nightly_rate',
      unit: '$',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Nightly Rate',
        timePeriod: 'ltm',
        source: '',
        definition: [...KPI_DEFINITIONS.ADR_market_aggregated.definitionLines] as string[],
        ctaText: KPI_DEFINITIONS.ADR_market_aggregated.learnMoreLinkText,
        ctaURL: LEARN_MORE_LINK.adr,
      },
    },
    seasonality: {
      displayName: 'Seasonality',
      key: 'seasonality',
      unit: '',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Seasonality',
        timePeriod: 'ltm',
        source: 'Airbnb Market Data (See detailed stats)',
        definition: [
          'This shows how revenue changes month to month compared to the yearly average. Calculated as the revenue difference between the highest and lowest months',
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.seasonality,
      },
    },
    trend: {
      displayName: 'Trend',
      key: 'trend',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Trend',
        timePeriod: 'ltm',
        source: 'Airbnb Market Data (See detailed stats)',
        definition: ['This shows year-over-year changes in revenue.'],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.trend,
      },
    },
  },
  income: {
    // revpan: {},
    revenue: {
      displayName: 'Revenue',
      key: 'ltm_revenue',
      unit: '$',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Revenue',
        timePeriod: 'ltm',
        source: '',
        definition: [...KPI_DEFINITIONS.Revenue_market_aggregated.definitionLines] as string[],
        ctaText: KPI_DEFINITIONS.Revenue_market_aggregated.learnMoreLinkText,
        ctaURL: KPI_DEFINITIONS.Revenue_market_aggregated.ctaUrl,
      },
    },
  },
};

export const financialInputs: FinancialInputs = {
  expenses: {
    totalExpense: {
      displayName: 'Total Expense',
      key: 'total_expenses',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Total Expense',
        timePeriod: '',
        source: '',
        formula: 'Mortgage pay + Operating Expenses + Taxes',
        definition: ['Total Expenses includes operating expenses, taxes and either mortgage payments or long-term rent payments.'],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.totalExpense,
      },
    },
    netOperatingExpense: {
      displayName: 'Operating Expenses',
      key: 'annual_operating_expenses_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Operating Expenses',
        timePeriod: '',
        source: '',
        formula:
          'Sum of property management fees, lodging tax, maintenance, insurance, utilities (wifi, electricity, gas), tech cost, marketing cost, vacancy cost, and HOA fees',
        definition: [
          'Total amount of yearly expenses to operate a property. Typical operating expenses are property management fees, maintenance, insurance, utilities (wifi, electricity, gas), and HOA fees. ',
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.netOperatingExpense,
      },
    },
    monthlyRent: {
      displayName: 'Monthly Rent',
      key: 'monthly_rent',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['rent'],
      showAsDeafaultIn: ['rent'],
      tooltipConfig: {
        name: 'Monthly Rent',
        timePeriod: '',
        source: '',
        formula: 'Monthly rent payment for the property',
        definition: [
          'The monthly rent payment for the property when operating as a rental arbitrage business. This is a financing cost that is deducted from net operating income to calculate net profit.',
        ],
        ctaText: '',
        ctaURL: '',
      },
    },
    mortgagePay: {
      displayName: 'Mortgage Payment',
      key: 'mortgage_annual',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Mortgage Payment',
        timePeriod: '',
        source: '',
        formula: '',
        definition: ["Total amount of mortgage payments you'll make per year."],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.mortgagePayment,
      },
    },
    taxes: {
      displayName: 'Taxes',
      key: 'taxes_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Taxes',
        timePeriod: '',
        source: '',
        formula: 'Property Tax + Income Tax - Depreciation',
        definition: [],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.taxes,
      },
    },
  },
  initialInvestment: {
    totalCashInvested: {
      displayName: 'Total Cash Invested',
      key: 'total_cash_invested',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Taxes',
        timePeriod: '',
        source: '',
        formula: 'Downpayment + Startup Cost',
        definition: [],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.totalCashInvested,
      },
    },
    startupCost: {
      displayName: 'Startup Cost',
      key: 'startup_costs_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Startup Cost',
        timePeriod: '',
        source: '',
        formula: 'Remodeling + Home Furnishing + Closing Costs',
        definition: [
          'Startup Cost includes all the costs you need to purchase a property and start an Airbnb, but does not include downpayment.',
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.startupCost,
      },
    },
  },
};

export const performanceIndicators: PerformanceIndicators = {
  cashflow: {
    netOperatingIncome: {
      displayName: 'Net Operating Income (NOI)',
      key: 'net_operating_income',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Net Operating Income (NOI)',
        timePeriod: '',
        source: '',
        formula: 'Revenue - Net Operating Expenses',
        definition: [
          "Net Operating Income (NOI) shows annual cash flow before deducting either mortgage payments or long-term rent payments and taxes. It shows the rental property's cashflow after deducting operating expenses.",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.netOperatingIncome,
      },
    },
    netProfitBeforeTax: {
      displayName: 'Net Profit before Tax',
      key: 'yearly_net_profit_before_tax',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Net Profit before Tax',
        timePeriod: '',
        source: '',
        formula: 'Revenue - Net Operating Expenses - (Mortgage Payments or Long-term rent payments)',
        definition: [
          "Net Profit before Tax shows annual cash flow before deducting taxes It shows the rental property's cashflow after deducting operating expenses and either mortgage payments or long-term rent payments.",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.netProfitBeforeTax,
      },
    },
  },
  investmentMetricts: {
    cashOnCashReturn: {
      displayName: 'Cash on Cash Return',
      key: 'cash_on_cash_return',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Cash on Cash Return',
        timePeriod: '',
        source: '',
        formula: 'Yearly Net Profit before Tax / Total Cash Invested',
        definition: [
          "Cash-on-Cash Return measures the annual cash flow of an investment property as a percentage of the total cash invested. It's often used by real estate investors to evaluate the profitability of a property. This is especially interesting for investors using financing, as they could generate relatively high cash flow compared to the amount of cash they initially invested. ",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.cashOnCashReturn,
      },
    },
    capRate: {
      displayName: 'Cap Rate',
      key: 'cap_rate',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: ['buy'],
      tooltipConfig: {
        name: 'Cap Rate',
        timePeriod: '',
        source: '',
        formula: 'Net Operating Income / Current Market Value',
        definition: [
          'The cap rate (short for capitalization rate) is a simple way to measure how much money a property earns compared to its price. It’s calculated by dividing the annual profit (after expenses) by the property’s price and turning that into a percentage.',
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.capRate,
      },
    },
    grossRentalYield: {
      displayName: 'Gross Rental Yield',
      key: 'gross_rental_yield',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Gross Rental Yield',
        timePeriod: '',
        source: '',
        formula: 'Yearly Revenue / Purchase Price',
        definition: [
          "Rental yield is a measure of the return on a property based on its rental revenue (before opearting expenses) relative to the property's purchase price. ",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.grossRentalYield,
      },
    },
    netRentalYield: {
      displayName: 'Net Rental Yield',
      key: 'net_rental_yield',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: ['buy'],
      tooltipConfig: {
        name: 'Net Rental Yield',
        timePeriod: '',
        source: '',
        formula: 'Net operating Income / Purchase Price',
        definition: [
          "Rental yield is a measure of the return on a property based on its rental income (after opearting expenses) relative to the property's purchase price.",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.netRentalYield,
      },
    },
    dscr: {
      displayName: 'DSCR',
      key: 'dscr',
      unit: '',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'DSCR',
        timePeriod: '',
        source: '',
        formula: 'Net operating Income / Total Debt',
        definition: [
          "DSCR (Debt Service Coverage Ratio) is a financial metric used to evaluate a property's ability to generate enough income to cover its debt obligations. It’s widely used by lenders to assess the risk of financing a real estate investment.",
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.dscr,
      },
    },
    roi: {
      displayName: 'ROI',
      key: 'annual_return_rate',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'ROI',
        timePeriod: '10 years average (or hold time)',
        source: '',
        formula:
          '((Total Operating Income + Sale Price – Acquisition Cost) / Acquisition cost) / Period',
        definition: [
          'ROI is the amount of value a rental property is adding per year compared to the cash you invested. This includes yearly property appreciation value and yearly net rental profit. ',
        ],
        ctaText: LEARN_MORE_TEXT,
        ctaURL: LEARN_MORE_LINK.roi,
      },
    },
    totalProfitWhenSold: {
      displayName: 'Total Profit when Sold',
      key: 'total_profit_when_sold',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Total Profit when Sold',
        timePeriod: '10 years (or hold time)',
        source: '',
        formula: 'Total Operating Income + Sale Price – Acquisition Cost',
        definition: [],
        ctaText: '',
        ctaURL: '',
      },
    },
  },
};

export const futureProjections = {
  tenYearsReturnRate: {
    displayName: '10 Years Return Rate',
    allowedModes: ['buy', 'manage'],
    showAsDeafaultIn: 'buy',
    key: 'ten_years_return_rate',
    unit: '%',
    legendMap: {
      value: 'Return Rate',
    },
    tooltipConfig: {
      name: '10 Years Return Rate',
      timePeriod: '',
      source: '',
      formula: '',
      definition: ['Estimated yearly return over a 10-year period, combining rental cashflow and resale value.'],
      ctaText: '',
      ctaURL: '',
    },
  },
  fiveYearsCashFlow: {
    displayName: '5 Years Accumulated Cashflow',
    allowedModes: ['buy', 'manage'],
    showAsDeafaultIn: 'manage',
    key: 'five_years_cash_flow',
    unit: '$',
    legendMap: {
      value: 'Cash Flow',
    },
    tooltipConfig: {
      name: '5 Years Accumulated Cashflow',
      timePeriod: '',
      source: '',
      formula: 'Example: Year 2 includes profits from Year 1 + Year 2.',
      definition: ['Shows cumulative net profit from a STR unit over time.'],
      ctaText: '',
      ctaURL: '',
    },
  },
  twentyFourMonthsBreakevenAnalysis: {
    displayName: '24 Months Breakeven Analysis',
    allowedModes: ['rent'],
    showAsDeafaultIn: 'rent',
    key: 'breakeven_analysis_24_months',
    unit: '$',
    legendMap: {
      total_expenses: 'Total Expense',
      total_net_operating_income: 'Net Income',
    },
    barColorsMap: {
      total_expenses: BAR_LESS_THAN_70_PERCENT,
      total_net_operating_income: BAR_MORE_THAN_70_PERCENT,
    },
    tooltipConfig: {
      name: '24 Months Breakeven Analysis',
      timePeriod: '',
      source: '',
      formula: 'Breakeven Value = -(Startup Cost - Net Profit before taxes), where Net Profit = Revenue - Net Operating Expenses - Monthly Rent',
      definition: [
        'Shows how long it takes to recover your initial investment when starting rental arbitrage.',
        'Negative values indicate how much you are away from breakeven. Positive values show profit beyond your recovered startup cost.',
      ],
      ctaText: '',
      ctaURL: '',
    },
  },
};
